import { useEffect } from 'react';
import { collection, doc, setDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';

export const useInitialData = () => {
  const { userProfile } = useAuth();

  useEffect(() => {
    const addSampleData = async () => {
      if (!userProfile || userProfile.role !== 'admin') return;

      try {
        // Check if sample data already exists
        const productsSnapshot = await getDocs(collection(db, 'products'));
        if (!productsSnapshot.empty) return; // Data already exists

        console.log('Adding sample data...');

        // Add sample products
        const sampleProducts = [
          {
            name: 'Wireless Bluetooth Headphones',
            platform: 'Amazon',
            productLink: 'https://amazon.com/dp/B08N5WRWNW',
            price: 79.99,
            asinId: 'B08N5WRWNW',
            addedBy: userProfile.uid,
            createdAt: new Date()
          },
          {
            name: 'Smart Fitness Tracker',
            platform: 'Amazon',
            productLink: 'https://amazon.com/dp/B07FKR6KXF',
            price: 149.99,
            asinId: 'B07FKR6KXF',
            addedBy: userProfile.uid,
            createdAt: new Date()
          },
          {
            name: 'Portable Phone Charger',
            platform: 'Flipkart',
            productLink: 'https://flipkart.com/portable-charger/p/itm123456',
            price: 29.99,
            addedBy: userProfile.uid,
            createdAt: new Date()
          }
        ];

        for (const product of sampleProducts) {
          await setDoc(doc(collection(db, 'products')), product);
        }

        // Add sample decision
        await setDoc(doc(collection(db, 'decisions')), {
          title: 'Choose New Office Location',
          description: 'We need to decide on our new office location. Please vote for your preferred option.',
          options: ['Downtown Tech Hub', 'Suburban Business Park', 'Remote-First (No Office)'],
          votes: {
            0: [userProfile.uid],
            1: [],
            2: []
          },
          createdBy: userProfile.uid,
          createdAt: new Date(),
          isActive: true
        });

        // Add sample announcements
        const sampleAnnouncements = [
          {
            title: 'Welcome to Lunexa Team Portal!',
            content: 'Welcome to our new team collaboration platform. Here you can participate in company decisions, manage products, chat with team members, and stay updated with announcements.',
            priority: 'high',
            createdBy: userProfile.uid,
            createdAt: new Date()
          },
          {
            title: 'Q1 Performance Review Meeting',
            content: 'Our quarterly performance review meeting is scheduled for next Friday at 2:00 PM. Please prepare your project updates and achievements for discussion.',
            priority: 'medium',
            createdBy: userProfile.uid,
            createdAt: new Date()
          }
        ];

        for (const announcement of sampleAnnouncements) {
          await setDoc(doc(collection(db, 'announcements')), announcement);
        }

        // Add sample discussion messages
        const sampleMessages = [
          {
            message: 'Hello everyone! Welcome to our team chat. Feel free to discuss anything work-related here.',
            authorId: userProfile.uid,
            authorName: userProfile.displayName || 'Admin User',
            createdAt: new Date(Date.now() - 3600000) // 1 hour ago
          },
          {
            message: 'This team portal looks great! Looking forward to using all the features.',
            authorId: userProfile.uid,
            authorName: userProfile.displayName || 'Admin User',
            createdAt: new Date(Date.now() - 1800000) // 30 minutes ago
          }
        ];

        for (const message of sampleMessages) {
          await setDoc(doc(collection(db, 'discussions')), message);
        }

        console.log('Sample data added successfully!');
      } catch (error) {
        console.error('Error adding sample data:', error);
      }
    };

    // Add sample data after a short delay to ensure user profile is loaded
    const timer = setTimeout(addSampleData, 2000);
    return () => clearTimeout(timer);
  }, [userProfile]);
};

export default useInitialData;